import * as tf from "@tensorflow/tfjs";
import { Logs } from "@tensorflow/tfjs";
import { ALL_MODELS, DATA_TYPES, MODEL_STATES } from "../constants";
import { logitsToNumber, to1DArray } from "../helpers/DataHelpers";
import { stateTypeF } from "../state";
import { BaseModel } from "./BaseModel";
import { dataSplit, infoType } from "./Dataset";

export class NeuralNet extends BaseModel {
  layers = 2;
  units = 16;
  epochs = 100;
  batchSize = 64;
  modelName = ALL_MODELS.neuralNet;
  lastUpdated = 0;

  model: tf.Sequential;
  bestModelWeights: tf.Tensor<tf.Rank>[];
  modelState = MODEL_STATES.ready;

  constructor(mlDataInfo: infoType) {
    super(mlDataInfo);
    this.initModel();
  }

  initModel() {
    const model = tf.sequential();
    for (let i = 1; i <= this.layers; i++) {
      let inputShape = this.units;
      if (i == 1) {
        inputShape = this.mlDataInfo.inputShape;
      }
      model.add(
        tf.layers.dense({
          units: this.units,
          inputShape: [inputShape],
          useBias: true,
        })
      );
      model.add(tf.layers.activation({ activation: "relu" }));
    }
    model.add(
      tf.layers.dense({
        units: this.mlDataInfo.outputShape,
        inputShape: [this.units],
      })
    );
    let optimizer = tf.train.adam();

    let lossType: string;
    if (this.mlDataInfo.outputType == DATA_TYPES.number) {
      lossType = "meanSquaredError";
    } else if (this.mlDataInfo.outputType == DATA_TYPES.categorical) {
      lossType = "sparseCategoricalCrossentropy";
      model.add(tf.layers.activation({ activation: "softmax" }));
    }

    // Prepare the model for training: Specify the loss and the optimizer.
    model.compile({ loss: lossType, optimizer: optimizer });

    this.model = model;
  }

  async train(trainData: dataSplit, state: stateTypeF) {
    state.setModelState(
      this.modelName,
      MODEL_STATES.training,
      state.saveState.modelTraining.modelStates
    );
    const xs = tf.tensor2d(trainData.xTrain, [
      trainData.xTrain.length,
      trainData.xTrain[0].length,
    ]);
    const ys = tf.tensor1d(trainData.yTrain);

    let weights = undefined;
    if (this.mlDataInfo.weights !== undefined) {
      weights = {};
      for (let i = 0; i < this.mlDataInfo.weights.length; i++) {
        weights[i] = this.mlDataInfo.weights[i];
      }
    }

    // Train the model using the data.
    await this.model.fit(xs, ys, {
      epochs: this.epochs,
      batchSize: this.batchSize,
      classWeight: weights,
      callbacks: {
        onBatchEnd: (batch: number, logs: Logs) =>
          this.onBatchEnd(batch, logs, trainData.xTest, trainData.yTest, state),
      },
    });
    this.model.setWeights(this.bestModelWeights);

    // Calc F1 Score
    const xTest = tf.tensor2d(trainData.xTest, [
      trainData.xTest.length,
      trainData.xTest[0].length,
    ]);

    state.setModelState(
      this.modelName,
      MODEL_STATES.trained,
      state.saveState.modelTraining.modelStates
    );
  }

  predict(X: number[][]) {
    let output = this.predictProbs(X);
    if (this.mlDataInfo.outputType == DATA_TYPES.number) {
      return to1DArray(output);
    } else if (this.mlDataInfo.outputType == DATA_TYPES.categorical) {
      return logitsToNumber(output);
    }
  }

  predictProbs(X: number[][]) {
    const tensor = tf.tensor2d(X, [X.length, X[0].length]);
    let outTensor = this.model.predict(tensor) as tf.Tensor<tf.Rank>;
    let output = outTensor.arraySync() as number[][];
    return output;
  }

  onBatchEnd(
    batch: number,
    logs: Logs,
    xTest: number[][],
    yTest: number[],
    state: stateTypeF
  ) {
    if (state.getTrainingCanceled()) {
      this.model.stopTraining = true;
    }
    if (batch == 0) {
      //Set model progress
      this.epochsComplete += 1;
      let progress = (100 * this.epochsComplete) / this.epochs;
      this.lastUpdated = BaseModel.setModelProgress(
        progress,
        state,
        this.modelName,
        this.lastUpdated
      );
      //Check for best model
      if (this.isBestModel(xTest, yTest)) {
        this.bestModelWeights = this.model
          .getWeights()
          .map((tensor) => tf.clone(tensor));
      }
    }
  }
}
